import React from 'react';
import { ArrowRight, Clock, BookOpen, Shield, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/Button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from './ui/Card';
import { Badge } from './ui/Badge';
interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  icon: React.ReactNode;
  date: string;
}
interface BlogSectionProps {
  onNavigate: (page: string) => void;
}
export function BlogSection({
  onNavigate
}: BlogSectionProps) {
  const posts: BlogPost[] = [{
    id: '1',
    title: 'Die 5 häufigsten Fehler bei der Hausratversicherung',
    excerpt: 'Unterversicherung und vergessene Wertsachen: Was du unbedingt beachten solltest, damit im Schadensfall wirklich alles ersetzt wird.',
    category: 'Hausrat',
    readTime: '4 Min. Lesezeit',
    icon: <AlertTriangle className="h-5 w-5 text-amber-500" data-id="element-14" />,
    date: '12. März 2024'
  }, {
    id: '2',
    title: 'Privathaftpflicht: Wann zahlt sie wirklich?',
    excerpt: 'Nicht jeder Schaden ist automatisch gedeckt. Wir erklären die wichtigsten Klauseln wie Gefälligkeitsschäden und Schlüsselverlust.',
    category: 'Haftpflicht',
    readTime: '5 Min. Lesezeit',
    icon: <Shield className="h-5 w-5 text-blue-500" data-id="element-15" />,
    date: '28. Feb. 2024'
  }, {
    id: '3',
    title: 'Berufsunfähigkeit: So früh wie möglich starten',
    excerpt: 'Warum junge Menschen oft nur einen Bruchteil zahlen und welche Gesundheitsfragen entscheidend für den späteren Schutz sind.',
    category: 'Vorsorge',
    readTime: '6 Min. Lesezeit',
    icon: <CheckCircle2 className="h-5 w-5 text-green-500" data-id="element-16" />,
    date: '15. Feb. 2024'
  }];
  return <section className="py-16 md:py-24 bg-slate-50 dark:bg-slate-950/50 relative overflow-hidden" data-id="element-17">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10" data-id="element-18">
        <div className="absolute top-1/3 right-0 h-96 w-96 rounded-full bg-blue-100/30 dark:bg-blue-900/10 blur-3xl translate-x-1/2" data-id="element-19"></div>
        <div className="absolute bottom-0 left-0 h-64 w-64 rounded-full bg-indigo-100/30 dark:bg-indigo-900/10 blur-3xl -translate-x-1/3 translate-y-1/3" data-id="element-20"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6" data-id="element-21">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12" data-id="element-22">
          <div className="max-w-2xl" data-id="element-23">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-900/20 px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800 mb-4" data-id="element-24">
              <BookOpen className="h-3.5 w-3.5" data-id="element-25" />
              <span data-id="element-26">Wissen & Insights</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-27">
              Ratgeber & Tipps
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-28">
              Expertenwissen einfach erklärt. Hier findest du Antworten auf die
              wichtigsten Fragen rund um deine Absicherung.
            </p>
          </div>
          <div className="hidden md:block" data-id="element-29">
            <Button variant="outline" onClick={() => onNavigate('leistungen')} className="group" data-id="element-30">
              Alle Artikel ansehen
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-31" />
            </Button>
          </div>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-32">
          {posts.map(post => <Card key={post.id} className="group flex flex-col h-full hover:shadow-xl hover:shadow-blue-500/5 dark:hover:shadow-blue-500/5 hover:-translate-y-1 transition-all duration-300 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-id="element-33">
              <CardHeader className="pb-4" data-id="element-34">
                <div className="flex items-center justify-between mb-4" data-id="element-35">
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" data-id="element-36">
                    {post.category}
                  </Badge>
                  <div className="h-8 w-8 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-colors" data-id="element-37">
                    {post.icon}
                  </div>
                </div>
                <CardTitle className="text-xl leading-tight mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-38">
                  {post.title}
                </CardTitle>
                <div className="flex items-center text-xs text-slate-400 dark:text-slate-500 gap-3" data-id="element-39">
                  <span className="flex items-center gap-1" data-id="element-40">
                    <Clock className="h-3 w-3" data-id="element-41" />
                    {post.readTime}
                  </span>
                  <span data-id="element-42">•</span>
                  <span data-id="element-43">{post.date}</span>
                </div>
              </CardHeader>

              <CardContent className="flex-grow pb-4" data-id="element-44">
                <CardDescription className="text-base line-clamp-3" data-id="element-45">
                  {post.excerpt}
                </CardDescription>
              </CardContent>

              <CardFooter className="pt-0" data-id="element-46">
                <Button variant="link" className="p-0 h-auto font-semibold text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300" onClick={() => onNavigate('leistungen')} data-id="element-47">
                  Weiterlesen
                  <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" data-id="element-48" />
                </Button>
              </CardFooter>
            </Card>)}
        </div>

        {/* Mobile CTA */}
        <div className="mt-8 md:hidden text-center" data-id="element-49">
          <Button variant="outline" onClick={() => onNavigate('leistungen')} className="w-full group" data-id="element-50">
            Alle Artikel ansehen
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-51" />
          </Button>
        </div>
      </div>
    </section>;
}